import { RATES_TTL_MS } from './rateCache'

/**
 * Fetches USD-based exchange rates with provider fallback.
 *
 * Primary : open.er-api.com — ExchangeRate-API open endpoint. Free, no API key,
 *           166 currencies, updated daily, returns its own next-update timestamp.
 * Fallback: @fawazahmed0/currency-api served from jsDelivr, then its Cloudflare
 *           mirror. Free, no key, no rate limits (static CDN files, updated daily).
 *
 * Uses native fetch on purpose — the shared axios instance in apiManager injects
 * auth headers that must not be sent to third-party hosts.
 */
const FETCH_TIMEOUT_MS = 8000
const ISO_CODE_RE = /^[A-Z]{3}$/

async function fetchJson(url, timeoutMs) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null
  try {
    const response = await fetch(url, { signal: controller ? controller.signal : undefined })
    if (!response.ok) throw new Error(`HTTP ${response.status} from ${url}`)
    return await response.json()
  } finally {
    if (timer) clearTimeout(timer)
  }
}

async function fetchFromErApi(timeoutMs) {
  const data = await fetchJson('https://open.er-api.com/v6/latest/USD', timeoutMs)
  if (!data || data.result !== 'success' || !data.rates || typeof data.rates !== 'object') {
    throw new Error('open.er-api.com returned an unexpected payload')
  }
  const now = Date.now()
  const nextUpdate = Number(data.time_next_update_unix) * 1000
  // Trust the API's own refresh schedule when sane, otherwise fall back to 24h
  const expiresAt = nextUpdate > now && nextUpdate <= now + 2 * RATES_TTL_MS
    ? nextUpdate
    : now + RATES_TTL_MS
  return { rates: data.rates, expiresAt, provider: 'open.er-api.com' }
}

function parseFawazPayload(data, provider) {
  const raw = data && data.usd
  if (!raw || typeof raw !== 'object') {
    throw new Error(`${provider} returned an unexpected payload`)
  }
  // Keys are lowercase and include crypto tickers — keep 3-letter ISO codes only
  const rates = {}
  Object.keys(raw).forEach(key => {
    const code = key.toUpperCase()
    if (ISO_CODE_RE.test(code) && typeof raw[key] === 'number') {
      rates[code] = raw[key]
    }
  })
  if (!Object.keys(rates).length) throw new Error(`${provider} returned no usable rates`)
  return { rates, expiresAt: Date.now() + RATES_TTL_MS, provider }
}

const PROVIDERS = [
  fetchFromErApi,
  async timeoutMs => parseFawazPayload(
    await fetchJson('https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json', timeoutMs),
    'currency-api (jsDelivr)',
  ),
  async timeoutMs => parseFawazPayload(
    await fetchJson('https://latest.currency-api.pages.dev/v1/currencies/usd.json', timeoutMs),
    'currency-api (Cloudflare)',
  ),
]

/**
 * Try each provider in order; resolve with the first successful result.
 * Throws only when every provider fails.
 */
export async function fetchUsdRates({ timeoutMs = FETCH_TIMEOUT_MS } = {}) {
  let lastError = null
  for (let i = 0; i < PROVIDERS.length; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await PROVIDERS[i](timeoutMs)
    } catch (error) {
      lastError = error
    }
  }
  throw lastError || new Error('All exchange-rate providers failed')
}
